import { Emitter } from "@core/events/emitter";
import type { EntitySnapshot } from "@models/data/entityData";
import type { IMergeModel, MergeChangedEvent } from "./IMergeModel";
import type { IProjectModel } from "./IProjectModel";

/**
 * `MergeModel` — see {@link IMergeModel} for why the web port expresses merge
 * as a snapshot flag rather than mesh surgery.
 *
 * Unity reference: `Models/MergeModel.cs`,
 * `SelectedGameObjectModel.MergeParts`/`UnmergeParts`, and the gating in
 * `ViewModels/EntityContextView.cs:806-809`.
 */
export class MergeModel implements IMergeModel {
  readonly onMergeChanged = new Emitter<MergeChangedEvent>();

  private readonly m_project: IProjectModel;

  constructor(project: IProjectModel) {
    this.m_project = project;
  }

  canMerge(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean {
    const targets = this.resolveTargets(sceneId, snapshotIds);
    if (targets.length === 0) return false;
    if (!targets.some(hasUnmergedChildren)) return false;
    return !targets.some((target) => this.containsSelectionSetMember(sceneId, target));
  }

  mergeBlockedBy(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
  ): "selection-set" | null {
    const targets = this.resolveTargets(sceneId, snapshotIds);
    if (targets.length === 0 || !targets.some(hasUnmergedChildren)) return null;
    return targets.some((target) => this.containsSelectionSetMember(sceneId, target))
      ? "selection-set"
      : null;
  }

  canUnmerge(sceneId: string, snapshotIds: ReadonlyArray<string>): boolean {
    const targets = this.resolveTargets(sceneId, snapshotIds);
    if (targets.length === 0) return false;
    // Unity gates un-merge on `!MergePartsIsVisible`, so a mixed selection
    // offers Merge only — never both.
    if (targets.some(hasUnmergedChildren)) return false;
    return targets.some((target) => target.hasMergedChildren);
  }

  async mergeAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>> {
    const targets = this.resolveTargets(sceneId, snapshotIds)
      .filter((target) => hasUnmergedChildren(target));
    return this.applyAsync(sceneId, targets.map((t) => t.id), true, signal);
  }

  async unmergeAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>> {
    const targets = this.resolveTargets(sceneId, snapshotIds);
    const ids: string[] = [];
    for (const target of targets) {
      // Un-merge reverts nested merges too: Unity clears the flag on the
      // target and on every descendant that still carries one.
      if (target.hasMergedChildren) ids.push(target.id);
      for (const descendant of flatten(target.children)) {
        if (descendant.hasMergedChildren) ids.push(descendant.id);
      }
    }
    return this.applyAsync(sceneId, ids, false, signal);
  }

  private async applyAsync(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
    merged: boolean,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<string>> {
    const changed: string[] = [];
    for (const snapshotId of uniqueIds(snapshotIds)) {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      if (!snapshot || snapshot.hasMergedChildren === merged) continue;
      await this.m_project.updateEntitySnapshotAsync(
        sceneId,
        snapshotId,
        { hasMergedChildren: merged },
        signal,
      );
      changed.push(snapshotId);
    }
    if (changed.length > 0) {
      this.onMergeChanged.emit({ sceneId, snapshotIds: changed, merged });
    }
    return changed;
  }

  /**
   * Unity `EntityContextView.GetMergeObjects`: a model root cannot merge (its
   * first child is the origin marker, which the merge would swallow), so a
   * root redirects to the model root beneath it — `transform.GetChild(1)`,
   * which is `children[1]` here for the same reason
   * (`sceneEntityRenderer.ts` places the origin at `children[0]`).
   */
  private resolveTargets(
    sceneId: string,
    snapshotIds: ReadonlyArray<string>,
  ): ReadonlyArray<EntitySnapshot> {
    const out: EntitySnapshot[] = [];
    const seen = new Set<string>();
    for (const snapshotId of snapshotIds) {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      if (!snapshot) continue;
      const target = snapshot.isRoot && snapshot.children.length > 1
        ? snapshot.children[1]
        : snapshot;
      if (!target || seen.has(target.id)) continue;
      seen.add(target.id);
      out.push(target);
    }
    return out;
  }

  /**
   * Unity `IsAnyChildPartOfSelectionSet`: merging a subtree that contains a
   * set member would make that member unaddressable, so the button greys out.
   * The target itself being in a set is fine.
   */
  private containsSelectionSetMember(sceneId: string, target: EntitySnapshot): boolean {
    const members = new Set<string>();
    for (const set of this.m_project.getSelectionSets(sceneId)) {
      for (const id of set.EntitySnapshotIDs ?? []) members.add(id);
    }
    if (members.size === 0) return false;
    for (const descendant of flatten(target.children)) {
      if (members.has(descendant.id)) return true;
    }
    return false;
  }
}

/**
 * Unity `HasUnmergedChildren` — a direct child still carries a
 * `CampfireExposeToEditor`. Merging destroys that component across the whole
 * subtree, so on web "has children and is not merged" is the same statement.
 */
function hasUnmergedChildren(snapshot: EntitySnapshot): boolean {
  return snapshot.children.length > 0 && !snapshot.hasMergedChildren;
}

function flatten(
  snapshots: ReadonlyArray<EntitySnapshot>,
  out: EntitySnapshot[] = [],
): EntitySnapshot[] {
  for (const snapshot of snapshots) {
    out.push(snapshot);
    flatten(snapshot.children, out);
  }
  return out;
}

function uniqueIds(ids: ReadonlyArray<string>): string[] {
  return [...new Set(ids.filter(Boolean))];
}
